// src/features/tidsregistering/components/absence-planner/CreateAbsenceDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Stack,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  ListSubheader,
  FormHelperText,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { differenceInDays, format } from 'date-fns';
import StaffMember from '../../../../components/common/StaffMember';
import { useBalances } from '../../hooks/useBalances';
import { absencesAPI } from '../../services/absences';
import { sickLeaveTrackersAPI } from '../../services/sickLeaveTrackers';
import {
  ABSENCE_STATISTICAL_CATEGORIES,
  CATEGORY_LABELS,
  HOURS_PER_DAY,
  LONG_TERM_SICK_THRESHOLD,
  DOCTOR_NOTE_THRESHOLD,
} from '../../constants/absence';
import { ACCOUNT_HANDLING_TO_BALANCE } from '../../constants/balances';

function formatHours(hours) {
  const h = Number(hours);
  const days = h / HOURS_PER_DAY;
  if (days >= 1) {
    return `${days.toFixed(1)} dage (${h.toFixed(1)}t)`;
  }
  return `${h.toFixed(1)}t`;
}

/**
 * Shows the relevant balance for the selected registration code,
 * plus a live projection of how much this absence will consume.
 */
function BalanceStatusPanel({ balances, registrationCode, startDate, loading, projection, projectionLoading }) {
  if (!registrationCode || loading) return null;

  const balanceCode = ACCOUNT_HANDLING_TO_BALANCE[registrationCode.account_handling];
  if (!balanceCode) return null; // No balance needed (e.g. sick leave)

  const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);

  const matching = balances.find(
    (b) =>
      b.balance_code?.toUpperCase() === balanceCode &&
      new Date(b.period_start) <= startDateObj &&
      new Date(b.period_end) >= startDateObj,
  );

  if (!matching) {
    return (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        Ingen <strong>{balanceCode}</strong>-saldo fundet for perioden der indeholder{' '}
        {format(startDateObj, 'dd.MM.yyyy')}. Fraværet kan ikke oprettes.
      </Alert>
    );
  }

  const remaining = Number(matching.remaining_hours);
  const entitlement = Number(matching.entitlement_hours);
  const used = Number(matching.used_hours);

  const projectedUsage = projection ? Number(projection.hours_projected) : null;
  const remainingAfter = projection ? Number(projection.remaining_after) : null;

  const effectiveRemaining = remainingAfter !== null ? remainingAfter : remaining;
  const pct = entitlement > 0 ? Math.max(0, Math.min(100, (effectiveRemaining / entitlement) * 100)) : 0;
  const severity = effectiveRemaining <= 0 ? 'error' : effectiveRemaining < entitlement * 0.2 ? 'warning' : 'success';

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: `${severity}.light`,
        bgcolor: `${severity}.50`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="caption" fontWeight={600} color={`${severity}.dark`}>
          {balanceCode} saldo
        </Typography>
        <Chip
          label={`${format(new Date(matching.period_start), 'dd.MM.yy')} – ${format(new Date(matching.period_end), 'dd.MM.yy')}`}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: 10 }}
        />
      </Stack>

      <Stack direction="row" spacing={2} mb={0.75}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Optjent
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatHours(entitlement)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Brugt
          </Typography>
          <Typography variant="body2">{formatHours(used)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Tilbage
          </Typography>
          <Typography variant="body2" fontWeight={700} color={`${severity}.main`}>
            {formatHours(remaining)}
          </Typography>
        </Box>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={100 - pct}
        color={severity}
        sx={{ height: 6, borderRadius: 1, bgcolor: `${severity}.100` }}
      />

      {/* Projection row */}
      {(projectionLoading || projectedUsage !== null) && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: '1px dashed',
            borderColor: 'divider',
          }}
        >
          {projectionLoading ? (
            <LinearProgress sx={{ borderRadius: 1 }} />
          ) : (
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Dette fravær bruger
                </Typography>
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  − {formatHours(projectedUsage)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Tilbage efter
                </Typography>
                <Typography variant="body2" fontWeight={700} color={`${severity}.main`}>
                  {formatHours(remainingAfter)}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Shows sick leave tracker statistics when a sickleave code is selected.
 */
function SickLeaveContextPanel({ tracker, loading }) {
  if (loading) return <LinearProgress sx={{ borderRadius: 1 }} />;
  if (!tracker) return null;

  const { consecutive_days, current_spell_start, long_term_threshold_reached, last_doctor_note } = tracker;
  const daysToThreshold = LONG_TERM_SICK_THRESHOLD - consecutive_days;
  const spellStart = current_spell_start ? new Date(current_spell_start) : null;

  return (
    <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
        Aktuel sygehistorik
      </Typography>

      {long_term_threshold_reached ? (
        <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
          <strong>Langtidssygemelding</strong> — over {LONG_TERM_SICK_THRESHOLD} dage sammenhængende.
          Sygedagpenge overdraget til kommunen.
        </Alert>
      ) : consecutive_days >= 20 ? (
        <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
          <strong>{consecutive_days} sammenhængende sygedage.</strong>{' '}
          {daysToThreshold} dage til langtidstærskel ({LONG_TERM_SICK_THRESHOLD} dage).
        </Alert>
      ) : consecutive_days > 0 ? (
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
          {consecutive_days} sammenhængende sygedage
          {spellStart ? ` siden ${format(spellStart, 'dd.MM.yyyy')}` : ''}.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>
          Ingen aktuel sammenhængende sygeperiode registreret.
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Sammenhængende dage
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {consecutive_days}
          </Typography>
        </Box>
        {spellStart && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Forløbsstart
            </Typography>
            <Typography variant="body2">{format(spellStart, 'dd.MM.yyyy')}</Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Lægeerklæring
          </Typography>
          <Typography
            variant="body2"
            color={!last_doctor_note && consecutive_days > DOCTOR_NOTE_THRESHOLD ? 'warning.main' : 'text.primary'}
            fontWeight={!last_doctor_note && consecutive_days > DOCTOR_NOTE_THRESHOLD ? 600 : 400}
          >
            {last_doctor_note ? format(new Date(last_doctor_note), 'dd.MM.yyyy') : 'Ingen'}
          </Typography>
        </Box>
      </Stack>

      {!last_doctor_note && consecutive_days > DOCTOR_NOTE_THRESHOLD && (
        <Typography variant="caption" color="warning.dark" display="block" mt={0.75}>
          Overvej at anmode om lægeerklæring (over {DOCTOR_NOTE_THRESHOLD} dage).
        </Typography>
      )}
    </Box>
  );
}

const CODE_INFO_NOTES = {
  salaryReduction_out: { severity: 'info', text: 'Fratrækkes direkte i lønnen — ingen saldo påvirkes.' },
  sickLeaveFull_in: { severity: 'info', text: 'Fuldt sygefravær. Arbejdsgiver betaler de første 30 dage, herefter kommunen.' },
  sickLeave_in: { severity: 'info', text: 'Delvist sygefravær.' },
  none: null,
};

const CATEGORY_INFO_NOTES = {
  leave: { severity: 'info', text: 'Orlovsfravær. Kontakt HR ved spørgsmål om rettigheder og dagpenge.' },
};

/**
 * Shows a one-line contextual note based on the registration code's account_handling / category.
 */
function CodeInfoNote({ registrationCode }) {
  if (!registrationCode) return null;
  const note =
    CODE_INFO_NOTES[registrationCode.account_handling] ??
    CATEGORY_INFO_NOTES[registrationCode.statistical_category] ??
    null;
  if (!note) return null;
  return (
    <Alert severity={note.severity} sx={{ py: 0.5 }}>
      {note.text}
    </Alert>
  );
}

/**
 * Dialog for creating a new absence entry.
 *
 * Props:
 *   open             — boolean
 *   onClose          — () => void
 *   onSubmit         — (payload) => void
 *   staffMember      — StaffMember object (pre-filled, read-only)
 *   initialStartDate — Date  pre-filled from drag
 *   initialEndDate   — Date  pre-filled from drag
 *   registrationCodes — RegistrationCode[]
 *   loading          — boolean
 *   submitError      — string | null   policy violation message from API
 */
export default function CreateAbsenceDialog({
  open,
  onClose,
  onSubmit,
  staffMember,
  initialStartDate,
  initialEndDate,
  registrationCodes = [],
  loading = false,
  submitError = null,
}) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [registrationCodeId, setRegistrationCodeId] = useState('');
  const [asWish, setAsWish] = useState(false);
  const [projection, setProjection] = useState(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  const [tracker, setTracker] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [showDescriptions, setShowDescriptions] = useState(false);


  const { balances, loading: balancesLoading, fetchForStaff, clear: clearBalances } = useBalances();

  // Reset form and fetch balances + sick leave tracker when dialog opens
  useEffect(() => {
    if (open) {
      setStartDate(initialStartDate ?? new Date());
      setEndDate(initialEndDate ?? new Date());
      setRegistrationCodeId('');
      setAsWish(false);
      setProjection(null);
      setTracker(null);
      if (staffMember?.staff_member_id) {
        fetchForStaff(staffMember.staff_member_id);
        setTrackerLoading(true);
        sickLeaveTrackersAPI
          .getTracker(staffMember.staff_member_id)
          .then((res) => setTracker(res.data ?? null))
          .catch(() => setTracker(null))
          .finally(() => setTrackerLoading(false));
      }
    } else {
      clearBalances();
      setProjection(null);
      setTracker(null);
    }
  }, [open, initialStartDate, initialEndDate, staffMember?.staff_member_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch projection whenever code + dates are all set
  useEffect(() => {
    if (!registrationCodeId || !startDate || !endDate || !staffMember?.staff_member_id) {
      setProjection(null);
      return;
    }
    let cancelled = false;
    setProjectionLoading(true);
    absencesAPI
      .projectAbsence({
        staffMemberId: staffMember.staff_member_id,
        registrationCodeId,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
      })
      .then((res) => {
        if (!cancelled) setProjection(res.data);
      })
      .catch(() => {
        if (!cancelled) setProjection(null);
      })
      .finally(() => {
        if (!cancelled) setProjectionLoading(false);
      });
    return () => { cancelled = true; };
  }, [registrationCodeId, startDate, endDate, staffMember?.staff_member_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const absenceCodes = registrationCodes.filter((c) =>
    ABSENCE_STATISTICAL_CATEGORIES.includes(c.statistical_category),
  );

  const selectedCode = absenceCodes.find((c) => c.registration_code_id === registrationCodeId) ?? null;
  const canSubmit = !!registrationCodeId && startDate && endDate && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      staff_member_id: staffMember.staff_member_id,
      registration_code_id: registrationCodeId,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
      as_wish: asWish,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Opret fravær</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Staff member — read-only display */}
          {staffMember && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Medarbejder
              </Typography>
              <StaffMember staffMember={staffMember} avatarSize={32} clickable={false} />
            </Box>
          )}

          <Divider />

          {/* Registration code */}
          <FormControl fullWidth required>
            <InputLabel id="create-absence-dialog-select-label">Fraværskode</InputLabel>
            <Select labelId="create-absence-dialog-select-label"
              value={registrationCodeId}
              onChange={(e) => setRegistrationCodeId(e.target.value)}
              label="Fraværskode"
              endAdornment={
                <Tooltip title={showDescriptions ? 'Skjul beskrivelser' : 'Vis beskrivelser'}>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => { e.stopPropagation(); setShowDescriptions((v) => !v); }}
                    sx={{ mr: 2 }}
                  >
                    <InfoOutlinedIcon fontSize="small" color={showDescriptions ? 'primary' : 'disabled'} />
                  </IconButton>
                </Tooltip>
              }
            >
              {ABSENCE_STATISTICAL_CATEGORIES.flatMap((cat) => {
                const group = absenceCodes.filter((c) => c.statistical_category === cat);
                if (group.length === 0) return [];
                return [
                  <ListSubheader key={cat}>{CATEGORY_LABELS[cat] ?? cat}</ListSubheader>,
                  ...group.map((code) => (
                    <MenuItem key={code.registration_code_id} value={code.registration_code_id} sx={{ pl: 3, alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="body2">{code.title}</Typography>
                        {showDescriptions && code.description && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'normal', maxWidth: 380 }}>
                            {code.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  )),
                ];
              })}
            </Select>
            {selectedCode?.description ? (
              <FormHelperText>{selectedCode.description}</FormHelperText>
            ) : (
              <FormHelperText sx={{ color: 'text.disabled' }}>
                Tryk på ℹ for at se beskrivelser i dropdown
              </FormHelperText>
            )}
          </FormControl>

          {/* Balance status + projection for codes with a balance effect */}
          <BalanceStatusPanel
            balances={balances}
            registrationCode={selectedCode}
            startDate={startDate ?? new Date()}
            loading={balancesLoading}
            projection={projection}
            projectionLoading={projectionLoading}
          />

          {/* Sick leave tracker — shown only when a sickleave code is selected */}
          {selectedCode?.statistical_category === 'sickleave' && (
            <SickLeaveContextPanel tracker={tracker} loading={trackerLoading} />
          )}

          {/* Category/account-handling contextual note */}
          <CodeInfoNote registrationCode={selectedCode} />

          {/* Date range */}
          <>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="Fra dato"
                value={startDate}
                onChange={(d) => d && setStartDate(d)}
                maxDate={endDate ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="Til dato"
                value={endDate}
                onChange={(d) => d && setEndDate(d)}
                minDate={startDate ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
          </>

          {/* As wish toggle */}
          <FormControlLabel
            control={<Switch checked={asWish} onChange={(e) => setAsWish(e.target.checked)} />}
            label="Send som ønske (kræver godkendelse)"
          />

          {/* Inline API error — shows the actual policy violation message */}
          {submitError && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {submitError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuller
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {loading ? 'Opretter…' : 'Opret fravær'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
